import Swiper from "swiper";
import { Navigation, Thumbs, FreeMode } from "swiper/modules";

/**
 * Crea i due Swiper coordinati (thumbs + main) all'interno di una modale.
 * Il thumbs va creato PRIMA del main: `thumbs.swiper` riceve l'istanza
 * già pronta.
 *
 * @param {HTMLElement} modal
 * @returns {{main: Swiper, thumbs: Swiper}|null}
 */
function createModalSwiper(modal) {
  const mainEl = modal.querySelector(".cappelli-shootings__main");
  const thumbsEl = modal.querySelector(".cappelli-shootings__thumbs");
  const nextEl = modal.querySelector(".cappelli-shootings__next");
  const prevEl = modal.querySelector(".cappelli-shootings__prev");

  if (!mainEl || !thumbsEl) {
    return null;
  }

  const thumbs = new Swiper(thumbsEl, {
    modules: [FreeMode, Thumbs],
    slidesPerView: "auto",
    spaceBetween: 10,
    freeMode: true,
    watchSlidesProgress: true,
  });

  const main = new Swiper(mainEl, {
    modules: [Navigation, Thumbs],
    slidesPerView: 1,
    spaceBetween: 10,
    navigation: { nextEl, prevEl },
    thumbs: { swiper: thumbs },
  });

  return { main, thumbs };
}

/**
 * Gestisce un singolo blocco shootings: link -> modale con slider.
 *
 * @param {HTMLElement} el Wrapper .cappelli-shootings
 */
function initShootings(el) {
  const links = Array.from(el.querySelectorAll(".cappelli-shootings__link"));
  const modals = Array.from(el.querySelectorAll(".cappelli-shootings__modal"));

  if (!links.length || !modals.length) {
    return;
  }

  // Mappa index -> { el, swiper } per la lazy init.
  const byIndex = new Map();
  modals.forEach((modal) => {
    byIndex.set(modal.dataset.index, { el: modal, swiper: null });
  });

  let activeModal = null;
  let lastFocused = null;

  function openModal(index) {
    const entry = byIndex.get(String(index));
    if (!entry) {
      return;
    }

    lastFocused = document.activeElement;
    activeModal = entry.el;

    entry.el.classList.add("is-open");
    entry.el.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("cappelli-shootings-modal-open");

    // Prima apertura: creo gli Swiper. Aperture successive: solo update
    // (il main/thumbs erano in display:none, quindi da ricalcolare).
    requestAnimationFrame(() => {
      if (!entry.swiper) {
        entry.swiper = createModalSwiper(entry.el);
      } else {
        if (entry.swiper.thumbs) {
          entry.swiper.thumbs.update();
        }
        if (entry.swiper.main) {
          entry.swiper.main.update();
        }
      }

      const closeBtn = entry.el.querySelector(".cappelli-shootings__close");
      if (closeBtn) {
        closeBtn.focus();
      }
    });
  }

  function closeModal() {
    if (!activeModal) {
      return;
    }

    activeModal.classList.remove("is-open");
    activeModal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("cappelli-shootings-modal-open");
    activeModal = null;

    if (lastFocused && typeof lastFocused.focus === "function") {
      lastFocused.focus();
    }
  }

  links.forEach((link) => {
    link.addEventListener("click", () => openModal(link.dataset.index));
  });

  // Chiusura: click sull'icona X o sul backdrop (elementi con data-close).
  modals.forEach((modal) => {
    modal.addEventListener("click", (e) => {
      if (e.target.closest("[data-close]")) {
        closeModal();
      }
    });
  });

  // Chiusura con ESC.
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && activeModal) {
      closeModal();
    }
  });
}

document.querySelectorAll(".cappelli-shootings").forEach(initShootings);
